import logging from "utils/logging";
import * as Constants from "./constants";
import Provider from "../../provider";
import { makeQuestionAvailableAction } from "../question/questionSlice";
import { updateGameStateAction } from "./stateActions";
import { determineNextRound } from "./roundCompletionUtils";
import _ from "lodash";
import { resetAllCaches, resetRoundCaches } from "../host/momentCacheManager";
import Analytics from "analytics";
import { EVENTS } from "analytics/events";

const log = logging.getLogger("Game State"); // eslint-disable-line no-unused-vars

const getNextState = (gameObject, players, currentQuestion) => {
  const { state: gameState } = gameObject;

  let nextState = { ...gameState };
  switch (gameState.currentState) {
    case Constants.GAMESTATE_PREGAME:
      nextState = {
        currentState: Constants.GAMESTATE_LOBBY
      };
      break;
    case Constants.GAMESTATE_LOBBY:
      if (!gameState.round) {
        // First time starting the game, go to instructions first
        nextState = {
          currentState: Constants.GAMESTATE_INSTRUCTIONS
        };
      } else {
        // Going to next round (already passed instructions in a previous round)
        // Use utility to dynamically determine next round based on available categories
        let nextRound = determineNextRound(gameState.round, gameObject.categories);
        if (!nextRound) {
          log.error(`Unable to determine next round after ${gameState.round}`);
          nextRound = Constants.GAMESTATE_ROUND_PRACTICE; // Fallback
        }
        nextState = {
          currentState: Constants.GAMESTATE_ROUND,
          round: nextRound,
          roundState: nextRound === Constants.GAMESTATE_ROUND_FINAL
            ? Constants.GAMESTATE_ROUNDSTATE_WAITING
            : Constants.GAMESTATE_ROUNDSTATE_ROUND_INTRO,
        };

        if (nextRound === Constants.GAMESTATE_ROUND_1) {
          // First player picks the category for round 1
          let firstPlayer = players?.[0];
          log.debug("First player for round 1: ", firstPlayer);
          if (firstPlayer) {
            nextState[Constants.GAMESTATE_FIELD_CONTROLLING_PLAYER] =
              firstPlayer.id;
          }
        } else if (/^round\d+$/.test(nextRound) && nextRound !== Constants.GAMESTATE_ROUND_1) {
          // Round 2+ — player in last place picks the category
          let lastPlayer = _.head(_.sortBy(players, ["score"]));
          if (lastPlayer) {
            nextState[Constants.GAMESTATE_FIELD_CONTROLLING_PLAYER] =
              lastPlayer.id;
          }
        } else if (nextRound === Constants.GAMESTATE_ROUND_FINAL) {
          log.debug(
            "Setting the question for the final round: ",
            gameObject.categories.final.question
          );
          nextState[Constants.GAMESTATE_FIELD_CURRENT_QUESTION] =
            gameObject.categories.final.question;
        }
      }
      break;
    case Constants.GAMESTATE_INSTRUCTIONS:
      // When transitioning from instructions, go to practice round
      nextState = {
        currentState: Constants.GAMESTATE_ROUND,
        round: Constants.GAMESTATE_ROUND_PRACTICE,
        roundState: Constants.GAMESTATE_ROUNDSTATE_ROUND_INTRO,
      };
      
      // Pick a random player to be in control for the practice round
      if (players && players.length > 0) {
        let firstPlayer = players[0];
        log.debug("First player for practice: ", firstPlayer);
        if (firstPlayer) {
          nextState[Constants.GAMESTATE_FIELD_CONTROLLING_PLAYER] = firstPlayer.id;
        }
      }
      break;
    case Constants.GAMESTATE_ROUND:
      switch (gameState.roundState) {
        case Constants.GAMESTATE_ROUNDSTATE_ROUND_INTRO:
          nextState = { ...gameState, roundState: Constants.GAMESTATE_ROUNDSTATE_WAITING };
          delete nextState.roundIntro;
          break;
        case Constants.GAMESTATE_ROUNDSTATE_WAITING:
          if (gameState.round === Constants.GAMESTATE_ROUND_FINAL) {
            const questionId = gameObject.categories.final.question;
             nextState = {
              ...gameState,
              roundState: Constants.GAMESTATE_ROUNDSTATE_FINALROUND_CATEGORY_PREVIEW,
              questionId,
            };
          }
          break;
        case Constants.GAMESTATE_ROUNDSTATE_FINALROUND_CATEGORY_PREVIEW:
          nextState = {
            ...gameState,
            roundState: Constants.GAMESTATE_ROUNDSTATE_FINALROUND_WAGER_INSTRUCTIONS,
          };
          break;
        case Constants.GAMESTATE_ROUNDSTATE_FINALROUND_WAGER_INSTRUCTIONS:
          nextState = {
            ...gameState,
            roundState: Constants.GAMESTATE_ROUNDSTATE_QUESTION_WAGERING,
          };
          break;
        case Constants.GAMESTATE_ROUNDSTATE_DAILYDOUBLE_PICKED:
          nextState = {
            ...gameState,
            roundState: Constants.GAMESTATE_ROUNDSTATE_QUESTION_WAGERING,
          };
          break;
        case Constants.GAMESTATE_ROUNDSTATE_QUESTION_WAGERING:
          // Final round inserts a dramatic intro narration step before reading
          nextState = {
            ...gameState,
            roundState: gameState.round === Constants.GAMESTATE_ROUND_FINAL
              ? Constants.GAMESTATE_ROUNDSTATE_FINALROUND_QUESTION_INTRO
              : Constants.GAMESTATE_ROUNDSTATE_QUESTION_READING,
          };
          break;
        case Constants.GAMESTATE_ROUNDSTATE_FINALROUND_QUESTION_INTRO:
          nextState = {
            ...gameState,
            roundState: Constants.GAMESTATE_ROUNDSTATE_QUESTION_READING,
          };
          break;
        case Constants.GAMESTATE_ROUNDSTATE_QUESTION_SELECTED:
          nextState = {
            ...gameState,
            roundState: Constants.GAMESTATE_ROUNDSTATE_QUESTION_READING,
          };
          break;
        case Constants.GAMESTATE_ROUNDSTATE_QUESTION_READING:
          const endTime = new Date();
          const savedTimers = gameObject.settings?.timers || {};
          let questionTime;
          if (gameState.round === Constants.GAMESTATE_ROUND_FINAL) {
            // Always use the fixed Final Jeopardy timer — custom timers
            // are for regular/DD questions only. The think music and reveal
            // flow depend on a consistent duration.
            questionTime = Constants.FINAL_ROUND_QUESTION_TIME_SECONDS;
          } else if (currentQuestion?.dailyDouble) {
            questionTime = savedTimers.dailyDouble ?? Constants.QUESTION_TIME_SECONDS;
          } else {
            questionTime = savedTimers.regularQuestion ?? Constants.QUESTION_TIME_SECONDS;
          }
          log.debug(`Allowing ${questionTime} seconds for answer`);
          endTime.setSeconds(endTime.getSeconds() + questionTime);
          nextState = {
            ...gameState,
            roundState: Constants.GAMESTATE_ROUNDSTATE_QUESTION_ANSWERING,
            endTime: endTime.toJSON(),
          };
          break;
        case Constants.GAMESTATE_ROUNDSTATE_QUESTION_ANSWERING:
          nextState = {
            ...gameState,
            roundState: Constants.GAMESTATE_ROUNDSTATE_QUESTION_JUDGING,
          };
          break;
        case Constants.GAMESTATE_ROUNDSTATE_QUESTION_JUDGING:
          nextState = {
            ...gameState,
            roundState: Constants.GAMESTATE_ROUNDSTATE_QUESTION_RESULTS,
          };
          if (gameState.round === Constants.GAMESTATE_ROUND_FINAL) {
            nextState.roundState =
              Constants.GAMESTATE_ROUNDSTATE_FINAL_REVEALING;
          }
          break;
        case Constants.GAMESTATE_ROUNDSTATE_QUESTION_RESULTS:
          // Default: go back to waiting for next pick
          // Round completion is detected by the JudgingComplete moment,
          // which has access to Redux question entities with picked status
          nextState = {
            currentState: Constants.GAMESTATE_ROUND,
            round: gameState.round,
            roundState: Constants.GAMESTATE_ROUNDSTATE_WAITING,
            questionId: null,
            playerInControl: gameState.playerInControl,
          };
          break;
        case Constants.GAMESTATE_ROUNDSTATE_QUESTION_REVEALING:
          if (gameState.round === Constants.GAMESTATE_ROUND_FINAL) {
            nextState = {
              currentState: Constants.GAMESTATE_CREDITS,
              round: gameState.round,
            };
          }
          break;
        case Constants.GAMESTATE_ROUNDSTATE_FINAL_REVEALING:
          nextState = {
            currentState: Constants.GAMESTATE_CREDITS,
            round: gameState.round,
          };
          break;
        case Constants.GAMESTATE_ROUNDSTATE_ROUND_COMPLETE:
          // Transition to inter-round lobby for ready-up
          // Keep current round so LOBBY logic can determine next round
          nextState = {
            currentState: Constants.GAMESTATE_LOBBY,
            round: gameState.round,
          };
          break;
        default:
          break;
      }
      break;
    default:
      break;
  }
  return nextState;
};

export const advanceState = async (gameObject, players, dispatch, currentQuestion) => {
  log.debug("Advancing state for game: ", gameObject);
  const { state: gameState } = gameObject;
  let newState = getNextState(gameObject, players, currentQuestion);
  const gameId = gameObject.id;
  log.debug("Advanced game state to: ", newState, gameId);

  // Track game started — lobby with no round means first launch
  if (
    gameState.currentState === Constants.GAMESTATE_LOBBY &&
    !gameState.round &&
    newState.currentState === Constants.GAMESTATE_INSTRUCTIONS
  ) {
    Analytics.track(EVENTS.GAME_STATE_CHANGED, {
      game_id: gameId,
      from_state: "lobby",
      to_state: "instructions",
      player_count: players.length,
      is_game_start: true,
    });
  }

  // Reset caches on key state transitions
  if (gameState.currentState === Constants.GAMESTATE_PREGAME && newState.currentState === Constants.GAMESTATE_LOBBY) {
    resetAllCaches();
  } else if (gameState.currentState === Constants.GAMESTATE_LOBBY && newState.currentState === Constants.GAMESTATE_ROUND) {
    resetRoundCaches();
  }

  // Capture scores at the start of a new round (for comparing changes at round end)
  // Store as history: roundStartScores[round] = { playerId: score }
  if (
    gameState.currentState === Constants.GAMESTATE_LOBBY &&
    newState.currentState === Constants.GAMESTATE_ROUND &&
    newState.round
  ) {
    log.info(`Capturing initial scores for round ${newState.round}`);
    const currentRoundScores = {};
    players.forEach(player => {
      currentRoundScores[player.id] = player.score || 0;
    });

    // Preserve existing round start scores and add new round
    const roundStartScores = gameState.roundStartScores || {};
    newState.roundStartScores = {
      ...roundStartScores,
      [newState.round]: currentRoundScores
    };
  }

  // Reset scores when transitioning from ROUND_COMPLETE to LOBBY after practice round
  if (
    gameState.roundState === Constants.GAMESTATE_ROUNDSTATE_ROUND_COMPLETE &&
    gameState.round === Constants.GAMESTATE_ROUND_PRACTICE &&
    newState.currentState === Constants.GAMESTATE_LOBBY
  ) {
    log.info("Resetting scores after practice round");
    await Provider.resetScores(gameId);
  }

  if (
    newState.roundState === Constants.GAMESTATE_ROUNDSTATE_FINAL_REVEALING
  ) {
    // Safety net: close any unclosed wager-only submissions before staging
    // scores. Idempotent — no-op if FinalAnsweringTimeout already closed them.
    try {
      await Provider.closeQuestionSubmissions(gameId, newState.questionId);
    } catch (err) {
      log.error('Failed to close submissions before reveal:', err);
    }
    await Provider.prepareFinalReveals(gameId);
    await Provider.generateSuperlatives(gameId);
    // Create the conversational narration session for the reveal sequence.
    // The cloud function reads game data (players, scores, answers, wagers)
    // and builds a system prompt so the LLM has full context across all turns.
    const conversationId = await Provider.createFinalRevealConversation(gameId);
    // Initialize final reveal tracking state on newState directly —
    // it gets persisted by the updateGameStateAction dispatch below.
    // Reveal order is determined by preFinalScore (lowest first) —
    // the actual ordering happens in FinalPlayerReveal moment using submission data.
    newState.finalReveal = {
      currentPlayerId: null,
      currentStep: null,
      correctAnswerAcknowledged: false,
      conversationId,
    };
  } else if (
    newState.roundState === Constants.GAMESTATE_ROUNDSTATE_QUESTION_RESULTS
  ) {
    // If we're about to show results, save the score differences
    log.debug("Committing scores to leaderboard");
    const { data } = await Provider.commitSubmissionScoresToLeaderboard(
      gameId,
      newState.questionId
    );
    log.debug("Got new player in control: ", data);
    newState["playerInControl"] = data.playerInControl;
  } else if (
    newState.roundState === Constants.GAMESTATE_ROUNDSTATE_QUESTION_ANSWERING
  ) {
    log.info("Opening question for answers: ", newState);
    dispatch(
      makeQuestionAvailableAction(gameId, newState.questionId, newState.endTime)
    );
  }

  log.info("Setting new game state: ", newState);
  dispatch(updateGameStateAction(gameId, newState));
};

/**
 * Computes the next finalReveal sub-step and persists it via updateGameStateAction.
 *
 * Flow per player: player-intro → answer-intro → answer-reaction-wager-intro →
 *   wager-reaction → standings
 * At standings: loops to player-intro for next player, or advances to winner.
 * Returns null at winner — caller should advance to credits.
 *
 * @param {Object} gameObject - Full game object (has .id and .state)
 * @param {Function} dispatch - Redux dispatch
 * @returns {Promise<Object|null>} The new finalReveal state, or null if sequence is complete
 */
export const advanceFinalRevealStep = async (gameObject, dispatch) => {
  const gameId = gameObject.id;
  const finalReveal = gameObject.state?.finalReveal || {};
  const {
    currentStep,
    currentPlayerId,
    revealOrder = [],
    correctAnswerAcknowledged = false,
    conversationId,
  } = finalReveal;

  let nextStep;
  let nextPlayerId = currentPlayerId;

  // Per-player step progression (repeats for each player in revealOrder)
  const perPlayerSteps = [
    Constants.FINAL_REVEAL_STEP_PLAYER_INTRO,
    Constants.FINAL_REVEAL_STEP_ANSWER_INTRO,
    Constants.FINAL_REVEAL_STEP_ANSWER_REACTION_WAGER_INTRO,
    Constants.FINAL_REVEAL_STEP_WAGER_REACTION,
    Constants.FINAL_REVEAL_STEP_STANDINGS,
  ];

  switch (currentStep) {
    case null:
    case undefined:
      nextStep = Constants.FINAL_REVEAL_STEP_INTRO;
      break;

    case Constants.FINAL_REVEAL_STEP_INTRO:
      nextStep = Constants.FINAL_REVEAL_STEP_PLAYER_INTRO;
      nextPlayerId = revealOrder[0] || null;
      break;

    case Constants.FINAL_REVEAL_STEP_STANDINGS: {
      const currentIndex = revealOrder.indexOf(currentPlayerId);
      if (currentIndex < revealOrder.length - 1) {
        nextStep = Constants.FINAL_REVEAL_STEP_PLAYER_INTRO;
        nextPlayerId = revealOrder[currentIndex + 1];
      } else {
        nextStep = Constants.FINAL_REVEAL_STEP_WINNER;
      }
      break;
    }

    default: {
      const idx = perPlayerSteps.indexOf(currentStep);
      if (idx >= 0 && idx < perPlayerSteps.length - 1) {
        nextStep = perPlayerSteps[idx + 1];
      } else {
        log.error(`Unknown finalReveal step: ${currentStep}`);
        return null;
      }
      break;
    }
  }

  const newRevealState = {
    currentStep: nextStep,
    currentPlayerId: nextPlayerId,
    revealOrder,
    correctAnswerAcknowledged,
    conversationId,
  };

  log.info(`Advancing final reveal: ${currentStep} → ${nextStep} (player: ${nextPlayerId})`);

  const newState = {
    ...gameObject.state,
    finalReveal: newRevealState,
  };
  dispatch(updateGameStateAction(gameId, newState));

  return newRevealState;
};
